import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function CommandeSignaturePage() {
  const token = window.location.pathname.split('/').pop();
  const [info, setInfo]       = useState(null);
  const [error, setError]     = useState(null);
  const [signed, setSigned]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [nom, setNom]         = useState('');
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/ventes/signature-web/${token}`)
      .then(r => { setInfo(r.data); if (r.data.deja_signe) setSigned(true); setNom(r.data.signature?.signataire_nom || ''); })
      .catch(() => setError("Lien invalide ou expiré."));
  }, [token]);

  useEffect(() => {
    if (!info || signed) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1A3A6E'; ctx.lineWidth = 2.5; ctx.lineCap = 'round';

    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const t = e.touches?.[0] || e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const down = (e) => { e.preventDefault(); drawing.current = true; lastPos.current = pos(e); };
    const move = (e) => {
      e.preventDefault();
      if (!drawing.current) return;
      const p = pos(e);
      ctx.beginPath(); ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(p.x, p.y); ctx.stroke();
      lastPos.current = p;
    };
    const up = () => { drawing.current = false; };
    canvas.addEventListener('mousedown', down); canvas.addEventListener('mousemove', move); canvas.addEventListener('mouseup', up);
    canvas.addEventListener('touchstart', down, { passive: false });
    canvas.addEventListener('touchmove', move,  { passive: false });
    canvas.addEventListener('touchend', up);
    return () => {
      canvas.removeEventListener('mousedown', down); canvas.removeEventListener('mousemove', move); canvas.removeEventListener('mouseup', up);
      canvas.removeEventListener('touchstart', down); canvas.removeEventListener('touchmove', move); canvas.removeEventListener('touchend', up);
    };
  }, [info, signed]);

  const clearCanvas = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
  };

  const submit = async () => {
    if (!nom.trim()) { alert("Merci de saisir votre nom."); return; }
    const canvas = canvasRef.current;
    const blank = document.createElement('canvas');
    blank.width = canvas.width; blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) { alert("Merci de signer dans le cadre."); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/ventes/signature-web/${token}`, {
        signataire_nom: nom,
        signature_image_b64: canvas.toDataURL('image/png'),
      });
      setSigned(true);
    } catch { alert("Erreur lors de l'envoi de la signature."); }
    finally { setLoading(false); }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
        <div className="text-4xl mb-4">⚠️</div>
        <h2 className="text-lg font-bold text-gray-800 mb-2">Lien invalide</h2>
        <p className="text-gray-500 text-sm">{error}</p>
      </div>
    </div>
  );

  if (!info) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (signed) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-sm w-full text-center">
        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">Bon de commande signé</h2>
        <p className="text-gray-500 text-sm">Merci {info.signature?.signataire_nom}. Votre signature a bien été enregistrée.</p>
        <p className="text-gray-400 text-xs mt-4">Réf. : {info.numero}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto bg-white rounded-2xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b" style={{ background: '#1A3A6E' }}>
          <h1 className="text-white font-bold text-lg">Bon de Commande</h1>
          <p className="text-blue-200 text-sm mt-0.5">{info.numero} — {info.entreprise_nom}</p>
        </div>

        <div className="px-6 py-6 space-y-5">
          <p className="text-gray-600 text-sm">
            Bonjour <strong>{info.signature?.signataire_nom}</strong>, merci de signer ci-dessous pour confirmer votre commande.
          </p>

          {/* Nom */}
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Nom complet</label>
            <input value={nom} onChange={e => setNom(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Votre nom et prénom" />
          </div>

          {/* Canvas */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-xs font-semibold text-gray-600">Signature</label>
              <button onClick={clearCanvas} className="text-xs text-blue-600 hover:underline">Effacer</button>
            </div>
            <canvas ref={canvasRef} width={460} height={160}
              className="w-full border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 touch-none cursor-crosshair" />
          </div>

          <button onClick={submit} disabled={loading}
            className="w-full py-3 rounded-xl text-white font-bold text-sm transition-opacity hover:opacity-90 disabled:opacity-50"
            style={{ background: '#1A3A6E' }}>
            {loading ? 'Envoi en cours…' : 'Confirmer ma signature'}
          </button>
        </div>
      </div>
    </div>
  );
}
